/**
 * Billing page (route /billing) — round7/billing-ui (P4-B), a complete
 * rewrite of the round4/stripe-billing-portal scaffold (see DEBT.md's entry
 * of that name for the full history) against the now-amended P4-0/P4-A/P4-B
 * backend contracts: an Organization layer above tenants, a complete Stripe
 * backend (checkout, webhook, portal-session), and this repo's own
 * GET /api/portal/billing/prices (round7/billing-ui backend companion).
 *
 * === Same staging posture as the rest of Phase 4 ===
 * No real Stripe account exists yet (H-2, a human Stripe Dashboard setup
 * checklist, hasn't happened). This page is built COMPLETE against the real
 * API contracts and verified with mocked API responses — there is no real
 * Stripe checkout/portal-session redirect to walk through end-to-end in any
 * environment available to this task. That is an explicitly accepted gap
 * (see DockPro-Portal's DEBT.md, round4/stripe-billing-portal entry, updated
 * by this round), not something faked or silently skipped: every API call
 * below is real and correctly shaped, and the one thing that cannot be
 * proven here is "does clicking through actually work against Stripe's own
 * servers." GET /api/portal/billing/prices returning [] today (no prices
 * configured pre-H-2) is handled as a genuine, honest UI state below, not a
 * broken empty grid — see the no-subscription rendering.
 *
 * === States, derived from the real contract, never a raw tier string ===
 * lib/billing.ts's deriveBillingViewState(tenant) picks one of:
 *   - parent-org        (tenant.billedAt === "parent-org", checked FIRST,
 *                        before any status logic — see that function's own
 *                        doc comment for the full precedence rule, including
 *                        how "unpaid"/"canceled"/"suspended" are folded into
 *                        no-subscription and where "trialing" sits relative
 *                        to past_due/parent-org.)
 *   - no-subscription   (the plan-picker grid; carries `lapsedTrial: boolean`
 *                        — round7/signup-ui — true for a self-registered
 *                        tenant whose trial ended with no conversion, which
 *                        swaps the subtitle copy above the SAME grid; see
 *                        the JSX below)
 *   - active            (carries `cancelAtPeriodEnd: boolean` — true renders
 *                        the "cancels-at-period-end" variant inline, see
 *                        below, rather than as a fully separate branch)
 *   - past_due          (a warning banner + Manage billing only, per spec)
 *   - trialing          (round7/signup-ui, P4.5-B — a self-registered tenant
 *                        still inside its 30-day no-card trial: a banner
 *                        showing `daysRemaining`, above the SAME plan-picker
 *                        grid/checkout flow no-subscription uses — see the
 *                        JSX below for why these two states share one block)
 *
 * REAL GAP found while building this (see deriveBillingViewState's own doc
 * comment for the full investigation): `status` alone cannot distinguish
 * "never checked out" from "actively subscribed" — DockPro-Backend's
 * provisionTenant.ts stamps status: "active" on every tenant unconditionally
 * at creation, with no Stripe involvement, and checkout itself never writes
 * `status` either. The no-subscription/active split below is therefore keyed
 * on `currentPeriodEnd`/`cancelAtPeriodEnd` PRESENCE (only ever written once
 * a real subscription webhook has landed), not on `status` values as the
 * spec's own wording ("no active/trialing/past_due signal") literally
 * suggests — that literal reading would have shown every never-subscribed
 * tenant as "active".
 *
 * The "active"/past_due states NEVER display a plan/tier name — every fact
 * shown is derived from `entitlements` (submitLimitPerMonth/costOutputs/
 * editScope/templatesOnly) via lib/billing.ts's entitlementFacts(), per this
 * repo's Round 1 rule (GET /api/portal/tenant doesn't even return a raw
 * `tier` field to begin with). The ONLY place a tier string ("Mid"/"Top")
 * appears anywhere on this page is the no-subscription plan-picker's tier
 * headings — naming what's being SOLD is unavoidable for a pricing grid and
 * is a categorically different thing from describing the tenant's OWN
 * current plan; see lib/billing.ts's header for the same distinction spelled
 * out in code.
 *
 * === Judgment call: no organization-name field exists ===
 * The parent-org state's copy is generic ("your organization," no name).
 * `OrgRecord` (DockPro-Backend's lib/lambda/utils/org.ts) has NO `name`
 * field at all — the original spec's wording wanted "<org name>". Adding one
 * would be a backend schema change with no consumer yet (no enterprise org
 * exists — this state is unreachable in practice today), so it's flagged
 * here rather than silently invented: generic wording is shipped now: it is
 * correct, complete, and never blocks the first real enterprise location
 * from seeing a working page. Revisit if/when a real org-name field lands.
 *
 * === Judgment call: no "plan name" is ever invented for the tenant's OWN plan ===
 * The active/past_due states describe capabilities ("Unlimited
 * submissions", "150 submissions per month"), never "You're on the Top
 * plan" — deliberately. Inventing a display name for the tenant's current
 * plan would require either exposing the raw tier string (against this
 * repo's Round 1 rule) or reverse-engineering "which tier is this" from
 * entitlements (which the original round4 scaffold's own doc comment
 * already rejected as "exactly the inline tier-comparison logic the
 * architecture is designed to avoid on the frontend" — still true here).
 * No plan-name field was added to the backend contract for this round; if
 * one is ever wanted, it should be a deliberate, named backend field (the
 * same posture as the org-name judgment call above), not a client
 * workaround.
 *
 * === cancels-at-period-end is a variant of "active", not a separate render ===
 * A synthesis, not an explicit spec requirement either way: the spec's own
 * cancels-at-period-end bullet only names two things ("Active until <date>"
 * + Manage billing), but doesn't say to HIDE the entitlements/usage facts
 * the plain "active" bullet shows — and a tenant about to lose access has
 * every reason to still see what they currently get until then. This page
 * reuses the active rendering wholesale and only swaps the renewal line's
 * wording (see the JSX below) rather than building a second, mostly-
 * duplicate branch.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/auth-context";
import {
  getTenant, getUsage, getBillingPrices, createCheckoutSession, createPortalSession,
  ApiError, type TenantConfig, type UsageResult, type BillingPrice,
  type BillingTier, type BillingInterval,
} from "../lib/api";
import {
  deriveBillingViewState, entitlementFacts, describeUsage, formatYearMonth,
  formatUnixSeconds, groupPricesByTier, tierLabel, intervalLabel,
  TIER_BLURBS, trialBannerMessage, type BillingViewState,
} from "../lib/billing";
import { StatusMessage } from "../components/StatusMessage";
import { friendlyErrorMessage } from "../components/friendlyErrorMessage";
import ui from "../styles/ui.module.css";
import styles from "./BillingPage.module.css";

// Plain top-level helper, deliberately outside the component: assigning
// `window.location.href` from inside a component/hook body trips this
// repo's react-hooks/immutability lint rule ("modifying a variable defined
// outside a component"), which guards render-phase purity. Navigation is an
// intentional side effect triggered from an event handler, not a render —
// isolating it here keeps the component itself lint-clean without
// suppressing the rule. Shared by both the checkout and portal-session
// redirects below — both are "send the browser to a Stripe-hosted URL".
function redirectTo(url: string): void {
  window.location.href = url;
}

// Known Stripe-adjacent error codes get specific, honest copy — the same
// "check err.code first, fall back to friendlyErrorMessage" pattern this
// repo's other known-code mappings already use (e.g. LeadDetailPage's
// template-save error mapping). This is what "ending AUD-014's exception"
// means beyond swapping <p> for <StatusMessage>: the raw backend message
// (e.g. literally "No Stripe price ids are configured...") no longer
// reaches the UI at all, on this page or any other.
function checkoutErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.code === "STRIPE_NOT_CONFIGURED") {
    return "Billing isn't set up for this environment yet.";
  }
  if (err instanceof ApiError && err.code === "INVALID_PRICE_ID") {
    return "That plan is no longer available — refresh the page and try again.";
  }
  return friendlyErrorMessage(err, "starting checkout");
}

function manageBillingErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.code === "NO_BILLING_CUSTOMER") {
    // Expected-unreachable in practice — this action is never offered in
    // the no-subscription state (the only state with no billing customer
    // yet). Kept as honest, specific copy rather than a silent fallback in
    // case that invariant is ever violated by a future change.
    return "Billing hasn't been set up for this account yet.";
  }
  return friendlyErrorMessage(err, "opening the billing portal");
}

// round7/signup-ui — the one line of copy that differs between the three
// tenants that all land on the SAME plan-picker grid below: a genuinely
// fresh never-subscribed tenant, a still-trialing tenant choosing their
// paid plan for the first time, and a tenant whose trial already lapsed
// with no conversion (correction 6 — distinguishable now that lapsedTrial
// is a real signal, not folded silently into the generic copy).
function planPickerSubtitle(viewState: Extract<BillingViewState, { kind: "no-subscription" | "trialing" }>): string {
  if (viewState.kind === "trialing") return "Choose your plan.";
  // 2026-09-10: the abandoned-signup case. The banner above the grid does
  // the explaining; this line just frames the picker as finishing, not
  // starting over.
  if (viewState.pendingCard) return "Finish setting up your account.";
  if (viewState.lapsedTrial) {
    return "Your trial ended — your data is safe. Subscribe to pick up where you left off.";
  }
  return "Choose a plan to get started.";
}

// Exported so the page test can assert the exact copy without retyping it.
export const PENDING_CARD_BANNER =
  "Your account isn't active yet — you left checkout before adding a card. " +
  "Pick your plan below to finish setting up. Your 30-day free trial starts the moment a card is on file, " +
  "and you won't be charged until it ends.";

interface PlanTierCardProps {
  tier: BillingTier;
  entries: BillingPrice[];
  selectedInterval: BillingInterval;
  onSelectInterval: (interval: BillingInterval) => void;
  onSubscribe: (priceId: string) => void;
  isOwner: boolean;
  pendingPriceId: string | null;
}

function PlanTierCard({
  tier, entries, selectedInterval, onSelectInterval, onSubscribe, isOwner, pendingPriceId,
}: PlanTierCardProps) {
  const active = entries.find((e) => e.interval === selectedInterval) ?? entries[0];
  const hasToggle = entries.length > 1;
  const isPending = pendingPriceId === active.priceId;
  const anyPending = pendingPriceId !== null;

  return (
    <section className={`${ui.card} ${styles.planCard}`}>
      <h2 className={styles.tierHeading}>{tierLabel(tier)}</h2>
      <p className={styles.planBlurb}>{TIER_BLURBS[tier]}</p>

      {hasToggle ? (
        <div
          className={styles.intervalToggle}
          role="group"
          aria-label={`${tierLabel(tier)} billing interval`}
        >
          {entries.map((entry) => (
            <button
              key={entry.interval}
              type="button"
              className={
                entry.interval === active.interval
                  ? styles.intervalOptionSelected
                  : styles.intervalOption
              }
              aria-pressed={entry.interval === active.interval}
              onClick={() => onSelectInterval(entry.interval)}
            >
              {intervalLabel(entry.interval)}
            </button>
          ))}
        </div>
      ) : (
        <p className={styles.intervalNote}>{intervalLabel(active.interval)} billing</p>
      )}

      {isOwner ? (
        <button
          type="button"
          className={ui.button}
          disabled={anyPending}
          onClick={() => onSubscribe(active.priceId)}
        >
          {isPending ? "Redirecting…" : `Subscribe to ${tierLabel(tier)}`}
        </button>
      ) : (
        <p className={styles.ownerOnlyNote}>Only the account owner can change billing plans.</p>
      )}
    </section>
  );
}

export function BillingPage() {
  const { user, signOut } = useAuth();

  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [usage, setUsage] = useState<UsageResult | null>(null);
  const [usageError, setUsageError] = useState<string | null>(null);

  const [prices, setPrices] = useState<BillingPrice[] | null>(null);
  const [pricesError, setPricesError] = useState<string | null>(null);
  const [selectedInterval, setSelectedInterval] = useState<Partial<Record<BillingTier, BillingInterval>>>({});

  const [pendingPriceId, setPendingPriceId] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const [isManagingBilling, setIsManagingBilling] = useState(false);
  const [manageError, setManageError] = useState<string | null>(null);

  // Mount-time load — mirrors SettingsPage.tsx's/LeadsPage.tsx's own
  // mount-effect shape exactly (no synchronous setState before the async
  // call: `isLoading`/`loadError` already start at their correct initial
  // values, so there's nothing to reset here). The user-triggered Retry
  // path is a separate function below, not reused here, since a click
  // handler (unlike an effect body) may reset state synchronously before
  // kicking off its own fetch.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    getTenant(user.idToken)
      .then((result) => { if (!cancelled) setTenant(result); })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          signOut();
          return;
        }
        setLoadError(friendlyErrorMessage(err, "loading your billing information"));
      })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [user, signOut]);

  // AUD-008-style in-place Retry for the load-error branch below — see
  // SettingsPage.tsx's handleRetryLoad for the identical pattern.
  const handleRetryLoad = useCallback(() => {
    if (!user) return;
    setLoadError(null);
    setIsLoading(true);
    getTenant(user.idToken)
      .then((result) => setTenant(result))
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          signOut();
          return;
        }
        setLoadError(friendlyErrorMessage(err, "loading your billing information"));
      })
      .finally(() => setIsLoading(false));
  }, [user, signOut]);

  const viewState = tenant ? deriveBillingViewState(tenant) : null;
  const viewKind = viewState?.kind;

  // Usage is only relevant to the "active" branch (including the
  // cancels-at-period-end variant) — past_due deliberately stays minimal
  // per spec, and no-subscription/parent-org have no usage to show yet.
  // No synchronous setUsageError(null) here (see the load effect's own
  // comment on why) — `usageError` already starts at its correct initial
  // `null` value for the first time this view is entered, which is the
  // only case this effect actually needs to handle for this page's lifetime.
  useEffect(() => {
    if (!user || viewKind !== "active") return;
    let cancelled = false;
    getUsage(user.idToken)
      .then((result) => { if (!cancelled) setUsage(result); })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          signOut();
          return;
        }
        setUsageError(friendlyErrorMessage(err, "loading your usage"));
      });
    return () => { cancelled = true; };
  }, [user, viewKind, signOut]);

  // The price catalog is only needed for the plan-picker — now shared by
  // BOTH no-subscription (including the lapsed-trial variant) AND trialing
  // (round7/signup-ui: "Choose your plan" reuses this exact same picker/
  // checkout flow — see the JSX below).
  useEffect(() => {
    if (!user || (viewKind !== "no-subscription" && viewKind !== "trialing")) return;
    let cancelled = false;
    getBillingPrices(user.idToken)
      .then((result) => { if (!cancelled) setPrices(result); })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          signOut();
          return;
        }
        setPricesError(friendlyErrorMessage(err, "loading available plans"));
      });
    return () => { cancelled = true; };
  }, [user, viewKind, signOut]);

  const handleSubscribe = useCallback(async (priceId: string) => {
    if (!user) return;
    setCheckoutError(null);
    setPendingPriceId(priceId);
    try {
      const { checkoutUrl } = await createCheckoutSession(user.idToken, priceId);
      redirectTo(checkoutUrl);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        signOut();
        return;
      }
      setCheckoutError(checkoutErrorMessage(err));
      setPendingPriceId(null);
    }
  }, [user, signOut]);

  const handleManageBilling = useCallback(async () => {
    if (!user) return;
    setManageError(null);
    setIsManagingBilling(true);
    try {
      const { portalUrl } = await createPortalSession(user.idToken);
      redirectTo(portalUrl);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        signOut();
        return;
      }
      setManageError(manageBillingErrorMessage(err));
      setIsManagingBilling(false);
    }
  }, [user, signOut]);

  if (!user) return null;

  if (isLoading) {
    return (
      <main id="main-content" className={ui.main}>
        <h1>Billing</h1>
        <p className={ui.loading}>Loading billing…</p>
      </main>
    );
  }

  if (loadError && !tenant) {
    return (
      <main id="main-content" className={ui.main}>
        <div className={ui.errorBox}>
          <h1>Billing</h1>
          <StatusMessage variant="error" message={loadError} className={ui.error} />
          <div className={ui.actions}>
            <button type="button" onClick={handleRetryLoad} className={ui.button}>Retry</button>
            <Link to="/leads" className={ui.buttonSecondary}>Back to leads</Link>
          </div>
        </div>
      </main>
    );
  }

  if (!tenant || !viewState) return null;

  const isOwner = user.role === "owner";
  const manageBillingControl = isOwner ? (
    <button
      type="button"
      className={ui.button}
      disabled={isManagingBilling}
      onClick={handleManageBilling}
    >
      {isManagingBilling ? "Redirecting…" : "Manage billing"}
    </button>
  ) : (
    <p className={styles.ownerOnlyNote}>Only the account owner can manage billing.</p>
  );

  return (
    <main id="main-content" className={`${ui.main} ${styles.main}`}>
      <div className={ui.pageHeader}>
        <div>
          <h1>Billing</h1>
          {viewState.kind === "active" && (
            <p className={ui.pageHint}>
              What your account can do — described by what it grants, not a plan name.
            </p>
          )}
        </div>
      </div>

      {viewState.kind === "parent-org" && (
        <p className={`${ui.note} ${styles.parentOrgNote}`}>
          Billing for this location is managed at your organization.
        </p>
      )}

      {(viewState.kind === "no-subscription" || viewState.kind === "trialing") && (
        <div className={styles.plansSection}>
          {/* round7/signup-ui: trialing's own banner sits ABOVE the exact
              same picker/checkout flow no-subscription already uses below
              — "Choose your plan" is a subtitle-copy variant of this one
              shared block, not a second, mostly-duplicate branch (same
              precedent as cancels-at-period-end reusing "active"'s
              rendering — see this file's header). */}
          {viewState.kind === "trialing" && (
            <StatusMessage
              variant="status"
              message={trialBannerMessage(viewState.daysRemaining)}
              className={ui.info}
            />
          )}
          {/* 2026-09-10: the abandoned-signup case. This tenant registered
              and left Stripe Checkout before adding a card, so nothing is
              being served yet. Choosing a plan here opens the SAME
              card-gated 30-day trial signup would have (the backend keys
              that off status === "pending_card") — so the copy must
              promise a trial, not a charge. */}
          {viewState.kind === "no-subscription" && viewState.pendingCard && (
            <StatusMessage
              variant="status"
              message={PENDING_CARD_BANNER}
              className={ui.info}
            />
          )}
          <p className={ui.pageHint}>{planPickerSubtitle(viewState)}</p>

          {pricesError && (
            <StatusMessage variant="error" message={pricesError} className={ui.error} />
          )}
          {checkoutError && (
            <StatusMessage variant="error" message={checkoutError} className={ui.error} />
          )}

          {prices === null && !pricesError && (
            <p className={ui.loading}>Loading available plans…</p>
          )}

          {prices !== null && prices.length === 0 && (
            <p className={ui.note}>
              Billing isn't set up yet for this environment — check back soon.
            </p>
          )}

          {prices !== null && prices.length > 0 && (
            <div className={styles.plansGrid}>
              {groupPricesByTier(prices).map(({ tier, entries }) => (
                <PlanTierCard
                  key={tier}
                  tier={tier}
                  entries={entries}
                  selectedInterval={selectedInterval[tier] ?? entries[0].interval}
                  onSelectInterval={(interval) =>
                    setSelectedInterval((prev) => ({ ...prev, [tier]: interval }))
                  }
                  onSubscribe={handleSubscribe}
                  isOwner={isOwner}
                  pendingPriceId={pendingPriceId}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {viewState.kind === "past_due" && (
        <div className={styles.pastDueSection}>
          <StatusMessage
            variant="error"
            message="There's a problem with your last payment. Update your payment method to keep your plan active."
            className={ui.warning}
          />
          {manageError && (
            <StatusMessage variant="error" message={manageError} className={ui.error} />
          )}
          {manageBillingControl}
        </div>
      )}

      {viewState.kind === "active" && (
        <div className={styles.activeGrid}>
          <section className={`${ui.card} ${styles.planSummary}`}>
            <h2 className={ui.cardTitle}>Your plan</h2>
            <p className={ui.cardHint}>Included in your subscription.</p>
            <ul className={styles.factsList}>
              {entitlementFacts(tenant.entitlements).map((fact) => (
                <li key={fact} className={styles.fact}>
                  <span className={styles.factMark} aria-hidden="true">✓</span>
                  {fact}
                </li>
              ))}
            </ul>

            {viewState.cancelAtPeriodEnd ? (
              <p className={`${ui.warning} ${styles.periodLine}`}>
                {tenant.currentPeriodEnd !== undefined
                  ? `Active until ${formatUnixSeconds(tenant.currentPeriodEnd)}.`
                  : "Your plan will end at the close of your current billing period."}
              </p>
            ) : (
              tenant.currentPeriodEnd !== undefined && (
                <p className={`${ui.subtle} ${styles.periodLine}`}>
                  Renews on {formatUnixSeconds(tenant.currentPeriodEnd)}.
                </p>
              )
            )}

            {manageError && (
              <StatusMessage variant="error" message={manageError} className={ui.error} />
            )}
            <div className={styles.planActions}>{manageBillingControl}</div>
          </section>

          <aside className={styles.usageColumn} aria-label="Usage">
            <section className={ui.card}>
              <h2 className={ui.cardTitle}>This month</h2>
              {usageError && (
                <StatusMessage variant="error" message={usageError} className={ui.error} />
              )}
              {usage && (
                <>
                  <p className={styles.usageFigure}>
                    <span className={ui.statValue}>{usage.submitCount}</span>
                    {usage.submitLimitPerMonth !== null && (
                      <span className={styles.usageLimit}> / {usage.submitLimitPerMonth}</span>
                    )}
                  </p>
                  <p className={ui.subtle}>
                    {describeUsage(usage)} ({formatYearMonth(usage.yearMonth)})
                  </p>
                  {usage.submitLimitPerMonth !== null && (
                    <div
                      className={styles.usageBar}
                      role="progressbar"
                      aria-label="Submissions used this month"
                      aria-valuemin={0}
                      aria-valuemax={usage.submitLimitPerMonth}
                      aria-valuenow={Math.min(usage.submitCount, usage.submitLimitPerMonth)}
                    >
                      <div
                        className={styles.usageFill}
                        style={{ transform: `scaleX(${Math.min(1, usage.submitCount / usage.submitLimitPerMonth)})` }}
                      />
                    </div>
                  )}
                </>
              )}
              {!usage && !usageError && <p className={ui.loading}>Loading usage…</p>}
            </section>
            {!isOwner && (
              <p className={ui.callout}>
                A rep sees this page read-only — only the account owner can change plans or
                open the billing portal.
              </p>
            )}
          </aside>
        </div>
      )}
    </main>
  );
}
